<script setup lang="ts">
interface Option {
  label?: string
  value?: any
  disabled?: boolean
  [key: string]: any
}

interface Props {
  options?: Option[] | string[]
  placeholder?: string
  disabled?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  color?: 'primary' | 'gray'
  variant?: 'outline' | 'none'
  optionAttribute?: string
  valueAttribute?: string
  icon?: string
  trailingIcon?: string
}

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  size: 'md',
  color: 'primary',
  variant: 'outline',
  optionAttribute: 'label',
  valueAttribute: 'value'
})

const modelValue = defineModel<any>()

const sizeClasses = {
  xs: 'text-xs px-2 py-1',
  sm: 'text-sm px-2.5 py-1.5',
  md: 'text-sm px-3 py-2',
  lg: 'text-base px-4 py-2.5',
  xl: 'text-base px-4 py-3'
}

const normalizedOptions = computed(() => {
  return props.options.map((opt: any) => {
    if (typeof opt === 'string') {
      return { label: opt, value: opt }
    }
    return {
      label: opt[props.optionAttribute] || opt.label || opt.name || String(opt),
      value: opt[props.valueAttribute] !== undefined ? opt[props.valueAttribute] : opt,
      disabled: opt.disabled
    }
  })
})
</script>

<template>
  <div class="relative">
    <div v-if="icon" class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
      <Icon :name="icon" class="h-5 w-5 text-gray-400" />
    </div>
    <select
      v-model="modelValue"
      :disabled="disabled"
      class="block w-full rounded-md shadow-sm transition-colors appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-0"
      :class="[
        sizeClasses[size],
        variant === 'outline'
          ? 'border border-gray-300 bg-white focus:border-primary-500 focus:ring-primary-500'
          : 'border-0 bg-transparent',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        icon ? 'pl-10' : '',
        'pr-10'
      ]"
    >
      <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
      <option
        v-for="option in normalizedOptions"
        :key="option.value"
        :value="option.value"
        :disabled="option.disabled"
      >
        {{ option.label }}
      </option>
    </select>
    <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
      <Icon :name="trailingIcon || 'i-heroicons-chevron-down'" class="h-5 w-5 text-gray-400" />
    </div>
  </div>
</template>
